import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {msg} from '@lit/localize';
import store from '../../../store';
import {conditional} from '../../../util/conditional';
import {getProductData} from '../../../util/getProductData';
import {pimText, pimTextDebug} from '../../../util/values';
import {selectImages, selectLanguage, selectSpcConfiguration} from '../../../selectors';
import {selectOlivioDesign, selectOlivioDesignIsFloracion, selectOlivioDesignIsSistema} from '../../olivioSelectors';
import {olivioDesignOptions} from '../../olivioModel';

@customElement('olivio-specsheet-layout-page')
export class OlivioSpecsheetLayoutPage extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				margin: 0;
			}

			.pageContainer {
				display: flex;
				justify-content: space-between;
				width: 100%;
				gap: 1cqw;
				padding-top: 2cqw;
			}

			.title {
				font: var(--font-specsheet-print-small-text);
				font-weight: bold;
				font-size: 1.4cqw;
				padding: 0;
				margin: 0;
			}

			.headline {
				font: var(--font-specsheet-print-small-bold);
				font-weight: bold;
				font-size: 1.4cqw;
				padding: 0;
				margin: 0;
			}

			.drawing {
				width: 30%
			}

			.tableContainer {
				width: 70%;
			}

			.imageContainer {
				text-align: center;
				width: 80%;
				margin-left: 0;
				margin-right: auto;
				display: flex;
				flex-direction: column;
				gap: 1cqw;
			}

			img {
				max-width: 100%;
			}

			.productImage {
				max-width: 100%;
			}

			.topView {
				width: 100%;
				aspect-ratio: 1/1;
				border: 1px solid lightgray;
			}

			.sideView {
				width: 100%;
				aspect-ratio: 1/4;
				border: 1px solid lightgray;
			}

		`]
	;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.productData = state.productData;
		this.status = state.status;
		this.lang = state.status.lang;
		this.lightProfile = state.lightProfile.data;
		this.spcConfiguration = selectSpcConfiguration(state);
		this.conditionalVariantValues = state.productData.conditionalVariantValues;
		this.print = state.status.media === 'print' || state.status.media === 'print2';
		this.images = selectImages(state);
	}

	getValueFromProductData(featureId, featureValueId) {
		const pd = getProductData(featureId, this.productData);
		return pd?.values?.find(val => val.valueId === featureValueId);
	}

	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}

	render() {
		const lang = selectLanguage(this.state);
		const designId = selectOlivioDesign(this.state);

		const designLabel = olivioDesignOptions.find((designOption) => designOption.id === designId).label[lang];
		const designDescription = olivioDesignOptions.find((designOption) => designOption.id === designId).description[lang];

		return html`
			<div class="pageContainer">

				<div class="drawing">
					<div class="imageContainer">
						${when(this.print && this.images, () => html`
							<img alt="top view" src="${this.images.top}" class="topView"/>
							<img alt="side view" src="${this.images.side}" class="sideView"/>
						`, () => html`
							<olivio-3d class="topView" staticView="top"></olivio-3d>
							<olivio-3d class="sideView" staticView="side"></olivio-3d>
						`)}
					</div>
				</div>
				<div class="tableContainer">
					<div class="title">
						<p class="headline">${designLabel} ${msg('Luminaire Configuration')}<br><br></p>
					</div>
					<olivio-specsheet-head-table></olivio-specsheet-head-table>
				</div>

			</div>
		`;
	}
}
